import { Component } from 'react';
import { connect } from 'react-redux';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { asField } from 'informed';
import { dateFnsLocales } from 'shared-locales';
import { DisplayFormattedDate } from 'components/common';
import { Icon } from 'components/common/icons';

Object.keys(dateFnsLocales).forEach((key) =>
  registerLocale(key, dateFnsLocales[key])
);

const DatePicker = connect((state) => state)(
  asField(
    class extends Component {
      state = {
        mounted: false,
        usePortal: false
      };

      componentDidMount() {
        // render the picker menu in a portal if we are in a modal
        let el = this.pickerElement;
        let usePortal = false;
        while (el && el.classList && !el.classList.contains('modal')) {
          el = el.parentElement;
        }
        if (el && el.classList && el.classList.contains('modal')) {
          usePortal = true;
        }
        this.setState({ mounted: true, usePortal });
      }

      handleChange = (date) => {
        const { fieldApi, onChange } = this.props;
        fieldApi.setValue(Date.parse(date));
        if (onChange) onChange(Date.parse(date));
      };

      handleClear = () => {
        const { fieldApi, onChange } = this.props;
        fieldApi.setValue(null);
        if (onChange) onChange(null);
      };

      renderCustomHeader = ({
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled
      }) => (
        <div className="datepicker-header-month">
          <button
            className="datepicker-header-month-button"
            aria-label={this.props.messages.commonPreviousMonth}
            disabled={prevMonthButtonDisabled}
            onClick={decreaseMonth}
            type="button"
          >
            <Icon icon="chevron-left" />
          </button>
          <div className="datepicker-header-month-value">
            <DisplayFormattedDate
              date={date}
              dateFormat="LLLL yyyy"
              dateFormatOptions={{
                locale: dateFnsLocales[this.props.language]
              }}
            />
          </div>
          <button
            className="datepicker-header-month-button"
            aria-label={this.props.messages.commonNextMonth}
            disabled={nextMonthButtonDisabled}
            onClick={increaseMonth}
            type="button"
          >
            <Icon icon="chevron-right" />
          </button>
        </div>
      );

      render() {
        const {
          additionalCalendarClassName = '',
          align = 'left',
          customInput,
          disabled = false,
          fieldState: { error, value },
          isClearable,
          language,
          messages,
          maxDate,
          minDate,
          placeholder,
          selected,
          simpleClear = false,
          showIcon = true,
          style,
          theme
        } = this.props;

        const { mounted, usePortal } = this.state;

        return (
          <div
            className="datepicker"
            ref={(el) => {
              this.pickerElement = el;
            }}
            style={style}
          >
            <ReactDatePicker
              calendarClassName={`datepicker-calendar ${additionalCalendarClassName}`}
              customInput={customInput}
              dateFormat="dd.MM.yyyy"
              dayClassName={(date) => 'datepicker-day'}
              disabled={disabled}
              locale={language}
              maxDate={maxDate}
              minDate={minDate}
              onChange={this.handleChange}
              placeholderText={placeholder}
              renderCustomHeader={this.renderCustomHeader}
              selected={selected !== undefined ? selected : value}
              {...(mounted && usePortal ? { portalId: 'datepicker-root' } : {})}
              {...(align === 'right' ? { popperPlacement: 'bottom-end' } : {})}
            />

            {showIcon ? (
              <Icon
                icon={['far', 'calendar-alt']}
                style={{
                  color: '#a1a8af',
                  fontSize: '1.4rem',
                  position: 'absolute',
                  right: 11,
                  top: 10
                }}
              />
            ) : null}

            {isClearable && value && !disabled ? (
              <div className="clear">
                {!simpleClear ? (
                  <button
                    className="clear-button"
                    onClick={this.handleClear}
                    type="button"
                  >
                    {messages.commonClear}
                  </button>
                ) : (
                  <button
                    className="simple-clear"
                    aria-label={messages.commonClear}
                    data-tooltip={messages.commonClear}
                    onClick={this.handleClear}
                  >
                    <Icon icon="times-circle" />
                  </button>
                )}
              </div>
            ) : null}

            {error ? <div className="error-message">{error}</div> : null}

            <style jsx>{`
              .datepicker {
                display: inline-block;
                position: relative;
                width: 100%;
              }
              .datepicker :global(.react-datepicker-wrapper) {
                width: 100%;
              }
              .datepicker :global(.react-datepicker__input-container) {
                background-color: white;
              }
              .datepicker :global(.react-datepicker__input-container input) {
                background-color: transparent;
                border: 1px solid
                  ${error ? theme.dangerColor : theme.lightGreyDark};
                border-radius: ${theme.inputBorderRadius};
                color: ${theme.textColor};
                font-family: inherit;
                font-size: 1.5rem;
                outline: 0;
                padding: 7px 10px;
                position: relative;
                transition: border-color 0.2s;
                width: 100%;
                z-index: 2;
              }
              .datepicker
                :global(
                  .react-datepicker__input-container
                    input.react-datepicker-ignore-onclickoutside
                ) {
                border-color: ${error ? theme.dangerColor : '#a1a8aa'};
              }
              .datepicker
                :global(.react-datepicker__input-container input:disabled) {
                background-color: ${theme.lightGreyLighter};
                color: ${theme.textColorLighter};
                opacity: 0.7;
              }
              .datepicker
                :global(
                  .react-datepicker__input-container
                    input::-webkit-input-placeholder
                ) {
                color: #999;
                opacity: 1;
              }
              .datepicker
                :global(
                  .react-datepicker__input-container input::-moz-placeholder
                ) {
                color: #999;
                opacity: 1;
              }
              .datepicker
                :global(
                  .react-datepicker__input-container
                    input::-ms-input-placeholder
                ) {
                color: #999;
                opacity: 1;
              }
              .datepicker
                :global(
                  .react-datepicker__input-container input:-ms-input-placeholder
                ) {
                color: #999;
                opacity: 1;
              }
              .datepicker :global(.react-datepicker-popper),
              :global(.datepicker-root .react-datepicker-popper) {
                margin-top: 8px;
                z-index: 3;
              }
              .datepicker :global(.react-datepicker),
              :global(.datepicker-root .react-datepicker) {
                border: 0;
                border-radius: ${theme.dropdownBorderRadius};
                box-shadow: ${theme.dropdownShadow};
                font-family: inherit;
                font-size: 1.5rem;
                padding: 12px;
              }
              .datepicker :global(.react-datepicker__triangle),
              :global(.datepicker-root .react-datepicker__triangle) {
                display: none;
              }
              .datepicker :global(.react-datepicker__header),
              :global(.datepicker-root .react-datepicker__header) {
                background-color: white;
                border: 0;
                padding: 0;
              }
              .datepicker :global(.datepicker-header-month),
              :global(.datepicker-root .datepicker-header-month) {
                color: ${theme.textColor};
                align-items: center;
                display: flex;
                padding: 0 5px;
              }
              .datepicker :global(.datepicker-header-month-button),
              :global(.datepicker-root .datepicker-header-month-button) {
                background-color: transparent;
                border: 0;
                border-radius: 50%;
                cursor: pointer;
                flex-grow: 0;
                flex-shrink: 0;
                font-size: 1.2rem;
                padding: 5px 0;
                text-align: center;
                transition: background-color 0.2s;
                width: 30px;
                height: 30px;
              }
              .datepicker :global(.datepicker-header-month-button:hover),
              :global(.datepicker-root .datepicker-header-month-button:hover) {
                background-color: ${theme.lightGrey};
              }
              .datepicker :global(.datepicker-header-month-value),
              :global(.datepicker-root .datepicker-header-month-value) {
                color: ${theme.textColorLight};
                flex-grow: 1;
                flex-shrink: 1;
                font-size: 1.5rem;
                font-weight: 700;
                padding-bottom: 1px;
              }
              .datepicker :global(.react-datepicker__current-month),
              :global(.datepicker-root .react-datepicker__current-month) {
                font-size: 1.5rem;
              }
              .datepicker :global(.react-datepicker__month),
              :global(.datepicker-root .react-datepicker__month) {
                margin: 0;
              }
              .datepicker :global(.react-datepicker__day-names),
              :global(.datepicker-root .react-datepicker__day-names) {
                padding: 5px 0 0;
              }
              .datepicker :global(.react-datepicker__day),
              .datepicker :global(.react-datepicker__day-name),
              :global(.datepicker-root .react-datepicker__day),
              :global(.datepicker-root .react-datepicker__day-name) {
                margin: 1px 5px;
                padding: 7px 0;
                text-align: center;
                width: 30px;
                height: 30px;
              }
              .datepicker :global(.react-datepicker__day-name),
              :global(.datepicker-root .react-datepicker__day-name) {
                color: ${theme.textColorLight};
                font-size: 1.3rem;
                font-weight: 700;
              }
              .datepicker :global(.react-datepicker__day),
              :global(.datepicker-root .react-datepicker__day) {
                border-radius: 30px;
                color: ${theme.textColorLight};
                font-size: 1.5rem;
                font-weight: 600;
                outline: 0;
                transition: background-color 0.2s;
              }
              .datepicker :global(.react-datepicker__day:hover),
              :global(.datepicker-root .react-datepicker__day:hover) {
                background-color: ${theme.lightGrey};
              }
              .datepicker :global(.react-datepicker__day--today),
              :global(.datepicker-root .react-datepicker__day--today) {
                background-color: ${theme.lightGrey};
              }
              .datepicker :global(.react-datepicker__day--outside-month),
              :global(.datepicker-root .react-datepicker__day--outside-month) {
                color: #a1a8af;
              }
              .datepicker :global(.react-datepicker__day--selected),
              .datepicker :global(.react-datepicker__day--selected:hover),
              .datepicker :global(.react-datepicker__day--keyboard-selected),
              .datepicker
                :global(.react-datepicker__day--keyboard-selected:hover),
              :global(.datepicker-root .react-datepicker__day--selected),
              :global(.datepicker-root .react-datepicker__day--selected:hover),
              :global(
                  .datepicker-root .react-datepicker__day--keyboard-selected
                ),
              :global(
                  .datepicker-root
                    .react-datepicker__day--keyboard-selected:hover
                ) {
                background-color: ${theme.textColor};
                color: white;
              }
              .datepicker :global(.react-datepicker__day--disabled),
              .datepicker :global(.react-datepicker__day--disabled:hover),
              :global(.datepicker-root .react-datepicker__day--disabled),
              :global(.datepicker-root .react-datepicker__day--disabled:hover) {
                background-color: white;
                color: ${theme.textColor};
                opacity: 0.5;
              }
              .clear {
                line-height: 1;
              }
              .clear-button {
                background-color: transparent;
                border: 0;
                color: ${theme.textColorLighter};
                cursor: pointer;
                display: inline-block;
                font-family: inherit;
                font-size: 1.3rem;
                font-weight: 600;
                outline: 0;
                padding: 2px 5px;
              }
              .clear-button:focus,
              .clear-button:hover {
                text-decoration: underline;
              }
              .simple-clear {
                background-color: transparent;
                border: 0;
                color: #999;
                cursor: pointer;
                font-size: 1.4rem;
                position: absolute;
                right: 28px;
                top: 9px;
                transition: color 0.3s;
                z-index: 5;
              }
              .simple-clear:hover {
                color: ${theme.textColor};
              }
              .error-message {
                color: ${theme.dangerColor};
                font-size: 1.3rem;
                padding: 3px;
              }
            `}</style>
          </div>
        );
      }
    }
  )
);

export default DatePicker;
